import React from 'react';
import { Input } from '../../../../components/input';
import editIcon from '../../../../../assets/icons/edit.png';
import { CompanyProfile, AddressData } from '../types';

interface AddressFieldProps {
  icon: any;
  label: string;
  companyProfile: CompanyProfile;
  isEditing: boolean;
  editingAddress: AddressData;
  loading: boolean;
  cepError?: string | null;
  cepLoading?: boolean;
  onStartEdit: (profile: CompanyProfile) => void;
  onSave: (addressData: AddressData) => void;
  onCancel: () => void;
  onAddressChange: (addressData: AddressData) => void;
  onCEPChange: (cepValue: string) => void;
}

export const AddressField: React.FC<AddressFieldProps> = ({
  icon,
  label,
  companyProfile,
  isEditing,
  editingAddress,
  loading,
  cepError,
  cepLoading,
  onStartEdit,
  onSave,
  onCancel,
  onAddressChange,
  onCEPChange
}) => {
  const getFullAddress = (): string => {
    const address = companyProfile?.address || '';
    const parts: string[] = [];
    if (address) parts.push(address);
    if (companyProfile?.address_number) parts.push(companyProfile.address_number);
    if (companyProfile?.address_complement) parts.push(companyProfile.address_complement);
    // Se address já é composto (ex.: "Rua, Bairro, Cidade, UF"), não duplicar bairro/cidade/UF
    const addressIsComposite = address.includes(',');
    if (!addressIsComposite) {
      if (companyProfile?.address_district) parts.push(companyProfile.address_district);
      if (companyProfile?.city_name) parts.push(companyProfile.city_name);
      if (companyProfile?.uf) parts.push(companyProfile.uf);
    }
    if (companyProfile?.postcode) parts.push(companyProfile.postcode);
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  if (isEditing) {
    const fullAddressDisplay = (() => {
      const address = companyProfile?.address || '';
      const street = address.includes(',') ? address.split(',')[0].trim() : address;
      const parts: string[] = [
        street,
        companyProfile?.address_number,
        companyProfile?.address_complement,
        companyProfile?.address_district,
        companyProfile?.city_name,
        companyProfile?.uf,
        companyProfile?.postcode
      ].filter(Boolean) as string[];
      return parts.join(', ');
    })();

    return (
      <div className='field-row editing'>
        {icon && <img src={icon} alt={label} className='field-icon' />}
        <div className='edit-controls address-edit'>
          <div className='address-fields'>
            <div className='address-field-cep-wrapper'>
              <Input
                name='company_postcode'
                label='CEP'
                mask='99999-999'
                value={editingAddress.postcode || companyProfile?.postcode || ''}
                onChange={(val) => {
                  const newAddress = { ...editingAddress, postcode: val };
                  onAddressChange(newAddress);
                  onCEPChange(val);
                }}
              />
              {cepError && (
                <span className='field-error cep-error' role='alert'>
                  {cepError}
                </span>
              )}
            </div>
            <Input
              name='company_address_number'
              label='Número'
              value={editingAddress.address_number || companyProfile?.address_number || ''}
              onChange={(val) => onAddressChange({ ...editingAddress, address_number: val })}
            />
            <Input
              name='company_address_complement'
              label='Complemento'
              value={editingAddress.address_complement || companyProfile?.address_complement || ''}
              onChange={(val) => onAddressChange({ ...editingAddress, address_complement: val })}
            />
          </div>
          <div className='address-full-field-wrapper' data-readonly='true'>
            <Input
              name='company_address_full'
              label='Endereço completo'
              readonly={true}
              disabled={true}
              value={fullAddressDisplay}
              onChange={() => {}}
            />
          </div>
          <div className='edit-actions'>
            <button 
              className='save-btn' 
              onClick={() => onSave(editingAddress)}
              disabled={loading || !!cepError || !!cepLoading}
              title={cepError ? 'Não pode salvar um CEP inválido' : cepLoading ? 'Aguarde a consulta do CEP' : undefined}
            >
              Salvar
            </button>
            <button 
              className='cancel-btn' 
              onClick={onCancel}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fullAddressText = getFullAddress();

  return (
    <div className='field-row'>
      {icon && <img src={icon} alt={label} className='field-icon' />}
      <div className='field-content'>
        <div className='address-tooltip-wrapper'>
          <span className='field-value address-full-value address-tooltip-text'>
            {fullAddressText}
          </span>
          <div className='address-tooltip-content'>
            {fullAddressText}
          </div>
        </div>
      </div>
      <img 
        src={editIcon} 
        alt='edit' 
        className='edit-icon' 
        onClick={() => onStartEdit(companyProfile)}
      />
    </div>
  );
};

